/**
 * ProfilesClient - Manage browser profiles for storing login state.
 *
 * @example
 * ```typescript
 * const morph = new MorphClient({ apiKey: '...' });
 *
 * // Create a profile + live setup session
 * const setup = await morph.browser.profiles.createProfile({
 *   name: 'LinkedIn',
 *   repoId: 'owner/repo'
 * });
 *
 * console.log('Sign in at:', setup.session.debugUrl);
 * // User logs in, then explicitly save
 * await setup.save();
 *
 * // Use profile in browser tasks
 * await morph.browser.execute({
 *   task: 'Check notifications',
 *   url: 'https://linkedin.com',
 *   profileId: profile.id
 * });
 * ```
 */

import { fetchWithRetry } from '../../utils/resilience.js';
import type { BrowserConfig } from '../types.js';
import {
  MorphValidationError,
  MorphAuthenticationError,
  parseAPIError,
} from '../errors.js';
import type {
  Profile,
  ProfileSetup,
  ProfileWithMethods,
  CreateProfileInput,
  ProfileListResponse,
  ProfileSession,
  ProfileSessionInput,
  SaveProfileSessionInput,
  ProfileStateResponse,
  RepoSummary,
  RepoListResponse,
  APIProfile,
  APIProfileSession,
  APIProfileStateResponse,
} from './types.js';
import {
  transformProfile,
  transformCreateInput,
  transformSession,
  transformSaveInput,
  transformStateResponse,
} from './types.js';

const DEFAULT_API_URL = process.env.MORPH_ENVIRONMENT === 'DEV'
  ? 'http://localhost:8000'
  : 'https://browser.morphllm.com';

/**
 * ProfilesClient class for managing browser profiles.
 *
 * Access via `morph.browser.profiles` on a MorphClient instance.
 */
export class ProfilesClient {
  private config: BrowserConfig;

  constructor(config: BrowserConfig) {
    this.config = config;
  }

  /**
   * Create a new browser profile and immediately start a live session.
   *
   * @param input - Profile creation parameters
   * @returns Profile setup handle with live URL + save()
   * @throws {MorphValidationError} If input validation fails
   * @throws {MorphProfileLimitError} If profile limit is exceeded
   * @throws {MorphAuthenticationError} If API key is missing or invalid
   *
   * @example
   * ```typescript
 * const setup = await morph.browser.profiles.createProfile({
 *   name: 'LinkedIn Production',
 *   repoId: 'owner/repo'
 * });
 * console.log(setup.session.debugUrl);
 * await setup.save();
   * ```
   */
  async createProfile(input: CreateProfileInput): Promise<ProfileSetup> {
    return createProfile(input, this.config);
  }

  /**
   * List all profiles for the authenticated user.
   *
   * @param repoId - Optional repository ID to filter by
   * @returns Array of profiles
   *
   * @example
   * ```typescript
   * // List all profiles
   * const allProfiles = await morph.browser.profiles.listProfiles();
   *
   * // List profiles for a specific repo
   * const repoProfiles = await morph.browser.profiles.listProfiles('owner/repo');
   * ```
   */
  async listProfiles(repoId?: string): Promise<Profile[]> {
    return listProfiles(this.config, repoId);
  }

  /**
   * Get a profile by ID with convenience methods.
   *
   * @param id - Profile ID
   * @returns Profile with attached methods
   * @throws {MorphNotFoundError} If profile is not found
   *
   * @example
   * ```typescript
   * const profile = await morph.browser.profiles.getProfile('profile-id');
   * const state = await profile.getState();
   * await profile.delete();
   * ```
   */
  async getProfile(id: string): Promise<ProfileWithMethods> {
    return getProfile(id, this.config);
  }

  /**
   * Update a profile by opening a live session (no rename).
   *
   * @param id - Profile ID
   * @returns Profile setup handle with live URL + save()
   */
  async updateProfile(id: string): Promise<ProfileSetup> {
    return updateProfile(id, this.config);
  }

  /**
   * Delete a profile.
   *
   * @param id - Profile ID
   * @throws {MorphNotFoundError} If profile is not found
   */
  async deleteProfile(id: string): Promise<void> {
    return deleteProfile(id, this.config);
  }

  /**
   * Start a browser session for profile setup.
   *
   * Returns a live URL where the user can sign into accounts.
   * After signing in, call `saveSession` to persist the state.
   *
   * @param input - Optional session parameters
   * @returns Session with debug URL
   *
   * @example
   * ```typescript
   * const session = await morph.browser.profiles.startSession();
   * console.log('Sign in at:', session.debugUrl);
   * // Open debugUrl in browser, user signs in...
   * await morph.browser.profiles.saveSession(session.sessionId, profile.id);
   * ```
   */
  async startSession(input?: ProfileSessionInput): Promise<ProfileSession> {
    return startProfileSession(this.config, input);
  }

  /**
   * Save browser state from a session to a profile.
   *
   * Call this after the user is done signing into accounts.
   * Extracts cookies, localStorage, and sessionStorage.
   *
   * @param sessionId - Browser session ID from startSession
   * @param profileId - Profile ID to save state to
   * @returns Updated profile with cookie domains
   */
  async saveSession(sessionId: string, profileId: string): Promise<Profile> {
    return saveProfileSession({ sessionId, profileId }, this.config);
  }

  /**
   * List available repo IDs (discovery).
   *
   * @returns Repo summaries with profile counts
   */
  async listRepos(): Promise<RepoSummary[]> {
    return listRepos(this.config);
  }

  /**
   * Get the presigned URL for a profile's state.
   *
   * Use this to download the raw state JSON for debugging
   * or to restore state manually.
   *
   * @param profileId - Profile ID
   * @returns State URL with expiry information
   */
  async getProfileState(profileId: string): Promise<ProfileStateResponse> {
    return getProfileState(profileId, this.config);
  }
}

// ============================================================================
// Validation Helpers
// ============================================================================

function validateCreateInput(input: CreateProfileInput): void {
  if (!input.name || typeof input.name !== 'string') {
    throw new MorphValidationError('name is required', 'name');
  }

  const trimmedName = input.name.trim();
  if (trimmedName.length === 0) {
    throw new MorphValidationError('name cannot be empty', 'name');
  }

  if (trimmedName.length > 100) {
    throw new MorphValidationError('name must be 100 characters or less', 'name');
  }

  if (!input.repoId || typeof input.repoId !== 'string') {
    throw new MorphValidationError('repoId is required', 'repoId');
  }

  if (input.repoId.trim().length === 0) {
    throw new MorphValidationError('repoId cannot be empty', 'repoId');
  }
}

function validateId(id: string, fieldName: string): void {
  if (!id || typeof id !== 'string') {
    throw new MorphValidationError(`${fieldName} is required`, fieldName);
  }

  if (id.trim().length === 0) {
    throw new MorphValidationError(`${fieldName} cannot be empty`, fieldName);
  }
}

// ============================================================================
// Standalone Functions
// ============================================================================

/**
 * Create a new browser profile.
 */
export async function createProfile(
  input: CreateProfileInput,
  config: BrowserConfig = {}
): Promise<ProfileSetup> {
  validateCreateInput(input);

  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/profiles`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(transformCreateInput(input)),
    },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const apiProfile: APIProfile = await response.json();
  const profile = transformProfile(apiProfile);
  const session = await startProfileSession(config, { profileId: profile.id });
  return buildProfileSetup(profile, session, config);
}

/**
 * List all profiles for the authenticated user.
 */
export async function listProfiles(
  config: BrowserConfig = {},
  repoId?: string
): Promise<Profile[]> {
  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const url = repoId
    ? `${apiUrl}/profiles?repo_id=${encodeURIComponent(repoId)}`
    : `${apiUrl}/profiles`;

  const response = await fetchWithRetry(
    url,
    { method: 'GET', headers },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const data: { profiles: APIProfile[] } = await response.json();
  return data.profiles.map(transformProfile);
}

/**
 * Get a profile by ID with convenience methods.
 */
export async function getProfile(
  id: string,
  config: BrowserConfig = {}
): Promise<ProfileWithMethods> {
  validateId(id, 'id');

  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/profiles/${encodeURIComponent(id)}`,
    { method: 'GET', headers },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const apiProfile: APIProfile = await response.json();
  const profile = transformProfile(apiProfile);

  // Attach convenience methods
  return {
    ...profile,
    getState: () => getProfileState(id, config),
    delete: () => deleteProfile(id, config),
  };
}

/**
 * Update a profile.
 */
export async function updateProfile(
  id: string,
  config: BrowserConfig = {}
): Promise<ProfileSetup> {
  validateId(id, 'id');
  const profile = await fetchProfile(id, config);
  const session = await startProfileSession(config, { profileId: profile.id });
  return buildProfileSetup(profile, session, config);
}

/**
 * Delete a profile.
 */
export async function deleteProfile(
  id: string,
  config: BrowserConfig = {}
): Promise<void> {
  validateId(id, 'id');

  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/profiles/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }
}

/**
 * Start a browser session for profile setup.
 */
export async function startProfileSession(
  config: BrowserConfig = {},
  input?: ProfileSessionInput
): Promise<ProfileSession> {
  if (!config.apiKey) {
    throw new MorphAuthenticationError();
  }

  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  // Transform input if provided
  const body = input?.profileId
    ? { profile_id: input.profileId }
    : {};

  const response = await fetchWithRetry(
    `${apiUrl}/profiles/session/start`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const apiSession: APIProfileSession = await response.json();
  return transformSession(apiSession);
}

/**
 * Save browser state from a session to a profile.
 */
export async function saveProfileSession(
  input: SaveProfileSessionInput,
  config: BrowserConfig = {}
): Promise<Profile> {
  validateId(input.sessionId, 'sessionId');
  validateId(input.profileId, 'profileId');

  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/profiles/session/save`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(transformSaveInput(input)),
    },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const apiProfile: APIProfile = await response.json();
  return transformProfile(apiProfile);
}

/**
 * List repo IDs available to the authenticated user/org.
 */
export async function listRepos(config: BrowserConfig = {}): Promise<RepoSummary[]> {
  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/repos`,
    { method: 'GET', headers },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const data = await response.json();
  const repos = Array.isArray(data?.repos) ? data.repos : [];

  return repos.map((repo: any) => ({
    repoId: repo.repo_id,
    repoFullName: repo.repo_full_name,
    profileCount: repo.profile_count ?? 0,
  })) satisfies RepoSummary[];
}

/**
 * Get the presigned URL for a profile's state.
 */
export async function getProfileState(
  profileId: string,
  config: BrowserConfig = {}
): Promise<ProfileStateResponse> {
  validateId(profileId, 'profileId');

  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/profiles/${encodeURIComponent(profileId)}/state`,
    { method: 'GET', headers },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const apiState: APIProfileStateResponse = await response.json();
  return transformStateResponse(apiState);
}

// ============================================================================
// Helpers
// ============================================================================

function buildHeaders(config: BrowserConfig): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (config.apiKey) {
    headers['Authorization'] = `Bearer ${config.apiKey}`;
  }
  return headers;
}

async function fetchProfile(id: string, config: BrowserConfig): Promise<Profile> {
  const apiUrl = config.apiUrl || DEFAULT_API_URL;
  const headers = buildHeaders(config);

  const response = await fetchWithRetry(
    `${apiUrl}/profiles/${encodeURIComponent(id)}`,
    { method: 'GET', headers },
    config.retryConfig
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    const requestId = response.headers.get('x-request-id') || undefined;
    throw parseAPIError(response.status, errorText, requestId);
  }

  const apiProfile: APIProfile = await response.json();
  return transformProfile(apiProfile);
}

function buildProfileSetup(profile: Profile, session: ProfileSession, config: BrowserConfig): ProfileSetup {
  return {
    profile,
    session,
    save: () => saveProfileSession({ sessionId: session.sessionId, profileId: profile.id }, config),
  };
}
